// Analytics: compare several locations across several games. Each vertical's Overview compares one
// location across games; this compares any mix of retail stores, concession stands or sections,
// gates (all or one) and restrooms (all or one) across up to 4 games or series averages.
// Same layout as the Overviews: compare bar (locations, games, time window), headline numbers,
// findings, a table of locations across the games and one chart through the day.
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { LinearProgress } from '@mui/material';
import AuthContext from '../../contexts/auth-context.js';
import { getTeam } from '../../util.js';
import Icon from '../../components/atoms/Icon.js';
import Spinner from '../../components/atoms/Spinner';
import { Panel, PanelHeader, InfoTip } from '../Concession/components/ConcessionUI';
import { ChartSegmented } from '../Concession/charts/chartTheme';
import { KpiCard } from '../Concession/live/LiveComponents';
import Presence from '../Concession/components/Presence';
import OverviewInsights from '../Concession/components/OverviewInsights';
import { TimeWindow } from '../Concession/ConcessionOverview';
import { queueColor } from '../Gates/gatesMetrics';
import { venueNowMs } from '../Concession/live/liveMetrics';
import { buildCatalog, subjectIndex, scheduleLocation, METRICS, METRIC_ORDER } from './analyticsSources';
import { fetchLocationDay, locationDayKey, scheduleAPI, subjectSeries } from './analyticsData';
import {
  summarizeDay, combineDays, buildChartLines, buildAnalyticsInsights, TABLE_STATS, formatSeconds, minuteToClock, relativeToStart,
} from './analyticsMetrics';
import AnalyticsCompareChart, { DashSample } from './AnalyticsCompareChart';
import QueueAlertsView from './QueueAlertsView';
import '../Concession/concession.css';
import '../Retail/retail.css';
import '../Gates/gates.css';
import './analytics.css';

// Queue alerts tab (concession alerts and response times across a season, see QueueAlertsView).
// Hidden for now; set to true to bring the tab back.
const SHOW_QUEUE_ALERTS = false;

const MAX_LOCATIONS = 8;
const MAX_GAMES = 4;

// Location colors (the Overview palette); game line styles
const LOCATION_COLORS = ['#3e58c0', '#e88559', '#9792ec', '#aa4851', '#76ba92', '#0bcaca', '#866263', '#3c6e5f', '#f1dc78'];
// Each game has a line style (one chart: lines are colored by location) and a color (split by location:
// one small chart per location, lines colored by game). Different hues from the location colors.
// Line styles: solid, very dotted, semi-dotted, then a mix of all three (long dash, short dash, dot).
const GAME_STYLES = [
  { dash: [], color: '#1f2937' },
  { dash: [2, 3], color: '#db2777' },
  { dash: [6, 5], color: '#0ea5e9' },
  { dash: [14, 4, 6, 4, 2, 4], color: '#a16207' },
];

const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '—');

// Image for a location (logo, gate or restroom photo), falling back to an icon
const FALLBACK_ICONS = {
  store: 'shop', stand: 'cup-straw', section: 'cup-straw', gates: 'door-open', gate: 'door-open', restrooms: 'badge-wc', restroom: 'badge-wc',
};
// Gate and restroom photos fill their box, like on the gates and facilities pages; logos stay whole
const PHOTO_KINDS = ['gate', 'restroom'];
// Whole gates and restrooms locations have no image (each gate or restroom shows its own)
const NO_ICON_KINDS = ['gates', 'restrooms'];
const SubjectIcon = ({ subject, size = 26 }) => {
  const [failed, setFailed] = useState(false);
  if (NO_ICON_KINDS.includes(subject.kind)) return null;
  return (
    <span className={`an-icon${PHOTO_KINDS.includes(subject.kind) && subject.image && !failed ? ' photo' : ''}`} style={{ width: size, height: size }}>
      {subject.image && !failed
        ? <img src={subject.image} alt="" onError={() => setFailed(true)} />
        : <Icon name={FALLBACK_ICONS[subject.kind] || 'geo-alt'} />}
    </span>
  );
};

const useDismiss = (open, setOpen) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);
  return ref;
};

// Keeps a dropdown on screen: when the button is near the right edge, the dropdown shifts left.
// Checked on every update, since picking a location adds a chip and moves the button while it's open.
const useOnScreen = () => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.left = '';
    const overflow = el.getBoundingClientRect().right - (window.innerWidth - 16);
    if (overflow > 0) el.style.left = `${-overflow}px`;
  });
  return ref;
};

// ---------------------------------------------------------------------------
// "Add location": grouped by vertical, with a search box. Stands, gates and restrooms expand to
// their sections, gates and restrooms.
// ---------------------------------------------------------------------------
const LocationPicker = ({ catalog, selectedKeys, colorOf, onToggle, full, onClose }) => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const panelRef = useOnScreen();
  const q = query.trim().toLowerCase();
  const matches = (s) => !q || s.name.toLowerCase().includes(q) || (s.parentName || '').toLowerCase().includes(q);

  const Row = ({ subject, child = false, expandable = false, isOpen = false, count = 0 }) => {
    const selected = selectedKeys.includes(subject.key);
    return (
      <div className={`an-pick-row${child ? ' child' : ''}${selected ? ' selected' : ''}`}>
        <button
          type="button"
          className="an-pick-main"
          onClick={() => onToggle(subject)}
          disabled={!selected && full}
          aria-pressed={selected}
        >
          <span className="an-pick-check" style={selected ? { background: colorOf(subject.key), borderColor: colorOf(subject.key) } : undefined}>
            {selected && <Icon name="check-lg" />}
          </span>
          <SubjectIcon subject={subject} size={child ? 22 : 26} />
          <span className="an-pick-text">
            <span className="an-pick-name">{child ? subject.shortName : subject.name}</span>
            {subject.sub && <span className="an-pick-sub">{subject.sub}</span>}
          </span>
          <span className="an-pick-tags">
            {subject.metrics.map((m) => <span key={m} className="an-tag">{METRICS[m].label}</span>)}
          </span>
        </button>
        {expandable && (
          <button
            type="button"
            className={`an-pick-expand${isOpen ? ' open' : ''}`}
            onClick={() => setExpanded((e) => ({ ...e, [subject.key]: !isOpen }))}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Hide' : `Show ${count}`}
          >
            {count}
            <Icon name="chevron-down" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div ref={panelRef} className="cx-ov-picker an-picker" role="dialog" aria-label="Pick locations">
      <div className="cx-ov-picker-head">
        <span>Pick up to {MAX_LOCATIONS} locations</span>
        <button type="button" className="cx-ov-chip-remove" aria-label="Close" onClick={onClose}>
          <Icon name="x-lg" />
        </button>
      </div>
      <div className="an-search">
        <Icon name="search" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search locations, sections, gates, restrooms"
          autoFocus
        />
      </div>
      <div className="an-pick-list">
        {catalog.map((group) => {
          const items = group.items
            .map((s) => ({ s, kids: (s.children || []).filter(matches) }))
            .filter(({ s, kids }) => matches(s) || kids.length);
          if (!items.length) return null;
          return (
            <div key={group.vertical} className="an-pick-group">
              <div className="an-pick-group-label">{group.label}</div>
              {items.map(({ s, kids }) => {
                const hasKids = (s.children || []).length > 0;
                // Searching opens every match; otherwise a parent opens on click
                const isOpen = hasKids && (q ? kids.length > 0 : !!expanded[s.key]);
                return (
                  <div key={s.key}>
                    <Row subject={s} expandable={hasKids && !q} isOpen={isOpen} count={(s.children || []).length} />
                    {isOpen && (q ? kids : s.children).map((c) => <Row key={c.key} subject={c} child />)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// "Add game": season, then each series' games and its average
// ---------------------------------------------------------------------------
const GamePicker = ({ games, league, isLiveDate, currentSeason, setCurrentSeason, gameItems, onToggleGame, onToggleSeries, full, onClose }) => {
  const seasons = useMemo(() => {
    const years = new Set();
    games.forEach((g) => g.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
    return Array.from(years).sort((a, b) => b - a);
  }, [games]);
  const past = (d) => new Date() > moment(d.date);
  const shown = games
    .filter((x) => past(x.dates[0]))
    .filter((x) => moment(x.dates[0].date).format('YYYY') === currentSeason);
  const itemFor = (key) => gameItems.find((g) => g.key === key);
  const panelRef = useOnScreen();

  return (
    <div ref={panelRef} className="cx-ov-picker" role="dialog" aria-label="Pick games">
      <div className="cx-ov-picker-head">
        <span>Pick up to {MAX_GAMES} games or series averages</span>
        <button type="button" className="cx-ov-chip-remove" aria-label="Close" onClick={onClose}>
          <Icon name="x-lg" />
        </button>
      </div>
      <div className="cx-season">
        <span className="cx-chart-segment-label">Season</span>
        <div className="cx-chart-segmented">
          {seasons.map((season) => (
            <button key={season} type="button" className={`cx-chart-segment ${currentSeason === season ? 'active' : ''}`} onClick={() => setCurrentSeason(season)}>
              {season}
            </button>
          ))}
        </div>
      </div>
      <div className="cx-game-list">
        {shown.map((series, i) => {
          // A series average uses finished games only (today's game is still in progress)
          const pastDates = series.dates.filter((d) => past(d) && !isLiveDate(d.date));
          const seriesKey = `series-${series.teamID}-${series.dates[0].date}`;
          const avgItem = itemFor(seriesKey);
          return (
            <div key={`${series.teamID}-${i}`} className="cx-game-row">
              <div className="cx-game-info">
                <img alt="Opponent" width={22} height={22} src={getTeam(league, series.teamID)?.imgURL} />
                <span className="cx-game-name">{getTeam(league, series.teamID)?.name}</span>
                <span className="cx-game-dates">
                  {moment(series.dates[0].date).format('MMM D')} - {moment(series.dates[series.dates.length - 1].date).format('MMM D')}
                </span>
              </div>
              <div className="cx-game-buttons">
                {series.dates.map((d, index) => {
                  if (!past(d)) return null;
                  const item = itemFor(`game-${d.date}`);
                  return (
                    <button
                      key={d.date}
                      type="button"
                      disabled={(!item && full) || !!avgItem}
                      onClick={() => onToggleGame(d, series, index)}
                      className={`btn cx-game-btn${item ? ' selected an-game-on' : ''}`}
                      title={avgItem ? 'This series is already averaged' : isLiveDate(d.date) ? 'Today: still in progress' : undefined}
                    >
                      {index + 1}
                      {isLiveDate(d.date) && <span className="pulsing-dot an-live-dot" />}
                    </button>
                  );
                })}
                {pastDates.length >= 2 && (
                  <button
                    type="button"
                    disabled={!avgItem && full}
                    onClick={() => onToggleSeries(series, pastDates)}
                    className={`btn cx-game-btn an-avg-btn${avgItem ? ' selected an-game-on' : ''}`}
                    title={`Average of the ${pastDates.length} games in this series`}
                  >
                    AVG
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function Analytics() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const league = authCtx.league;
  // 'compare' (locations across games) or 'alerts' (concession queue alerts across a season)
  const [view, setView] = useState('compare');
  const hasQueueStands = SHOW_QUEUE_ALERTS && (authCtx.authLocationData?.concessions || []).some((l) => l.sensorDataType !== 'count' && (l.sections || []).length > 0);

  const catalog = useMemo(() => buildCatalog(authCtx.authLocationData), [authCtx.authLocationData]);
  const index = useMemo(() => subjectIndex(catalog), [catalog]);

  // Schedule (from the first location, as before)
  const [games, setGames] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [currentSeason, setCurrentSeason] = useState(moment().format('YYYY'));
  useEffect(() => {
    if (!authCtx.loggedIn) {
      navigate('/auth');
      return;
    }
    const source = scheduleLocation(authCtx.authLocationData);
    if (!source) {
      setScheduleLoading(false);
      return;
    }
    scheduleAPI(source.vertical, source.location)(
      'get-location-current-schedule',
      { venue: authCtx.venue, locationId: source.location.id, measurement: source.location.measurement },
      (data, error) => {
        if (error) {
          if (error.status === 401) {
            authCtx.logout();
            navigate('/auth');
          } else {
            alert('Unable to fetch games. Please refresh the page and try again.');
          }
          setScheduleLoading(false);
          return;
        }
        const list = data || [];
        setGames(list);
        const years = new Set();
        list.forEach((g) => g.dates.forEach((d) => { if (new Date() > moment(d.date)) years.add(moment(d.date).format('YYYY')); }));
        const latest = Array.from(years).sort((a, b) => b - a)[0];
        if (latest) setCurrentSeason(latest);
        setScheduleLoading(false);
      },
      authCtx.token
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authCtx.loggedIn, authCtx.authLocationData]);

  // Selections. Locations keep their color and games their line style while selected.
  const [picked, setPicked] = useState([]); // [{ key, color }]
  const [gameItems, setGameItems] = useState([]); // [{ key, dates: [gameDate], series, index, dash }]
  const [averageGames, setAverageGames] = useState(false);
  const [timeRange, setTimeRange] = useState({ startOffset: -180, endOffset: 240 });

  const [locPickerOpen, setLocPickerOpen] = useState(false);
  const [gamePickerOpen, setGamePickerOpen] = useState(false);
  const locRef = useDismiss(locPickerOpen, setLocPickerOpen);
  const gameRef = useDismiss(gamePickerOpen, setGamePickerOpen);

  const colorOf = useCallback((key) => picked.find((p) => p.key === key)?.color, [picked]);

  const toggleSubject = (subject) => {
    if (picked.some((p) => p.key === subject.key)) {
      setPicked((prev) => prev.filter((p) => p.key !== subject.key));
      return;
    }
    if (picked.length >= MAX_LOCATIONS) return;
    const used = picked.map((p) => p.color);
    const color = LOCATION_COLORS.find((c) => !used.includes(c)) || LOCATION_COLORS[0];
    setPicked((prev) => [...prev, { key: subject.key, color }]);
  };

  const nextStyle = (items) => GAME_STYLES.find((st) => !items.some((g) => g.dash === st.dash)) || GAME_STYLES[0];
  const toggleGame = (gameDate, series, idx) => {
    const key = `game-${gameDate.date}`;
    setGameItems((prev) => {
      if (prev.some((g) => g.key === key)) return prev.filter((g) => g.key !== key);
      if (prev.length >= MAX_GAMES) return prev;
      return [...prev, { key, dates: [gameDate], series, index: idx, ...nextStyle(prev) }];
    });
  };
  const toggleSeries = (series, pastDates) => {
    const key = `series-${series.teamID}-${series.dates[0].date}`;
    setGameItems((prev) => {
      if (prev.some((g) => g.key === key)) return prev.filter((g) => g.key !== key);
      // A series average replaces that series' single games
      const rest = prev.filter((g) => !(g.dates.length === 1 && pastDates.some((d) => d.date === g.dates[0].date)));
      if (rest.length >= MAX_GAMES) return prev;
      const dates = [...pastDates].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      return [...rest, { key, dates, series, index: null, ...nextStyle(rest) }];
    });
  };
  useEffect(() => { if (picked.length >= MAX_LOCATIONS) setLocPickerOpen(false); }, [picked.length]);
  useEffect(() => { if (gameItems.length >= MAX_GAMES) setGamePickerOpen(false); }, [gameItems.length]);
  useEffect(() => { if (gameItems.length < 2) setAverageGames(false); }, [gameItems.length]);

  const subjects = picked.map((p) => ({ subject: index.get(p.key), color: p.color })).filter((s) => s.subject);

  // Today's game (venue time; the game day runs until 3 AM) is still in progress: its numbers are "so far"
  const venueTz = scheduleLocation(authCtx.authLocationData)?.location?.timeZone;
  const todayKey = moment(venueNowMs(venueTz)).subtract(3, 'hours').format('YYYY-MM-DD');
  const isLiveDate = useCallback((date) => moment(date).format('YYYY-MM-DD') === todayKey, [todayKey]);

  const gameLabel = useCallback((item) => {
    const opponent = getTeam(league, item.series?.teamID)?.name || null;
    if (item.dates.length > 1) {
      const first = moment(item.dates[0].date);
      const last = moment(item.dates[item.dates.length - 1].date);
      const range = `${first.format('MMM D')}–${first.month() === last.month() ? last.format('D') : last.format('MMM D')}`;
      return { short: `${range} avg`, long: `${range} (avg of ${item.dates.length})`, sub: `${opponent ? `vs ${opponent} · ` : ''}Average of ${item.dates.length} games`, opponent, isAverage: true };
    }
    const d = moment(item.dates[0].date);
    return {
      live: isLiveDate(item.dates[0].date),
      short: d.format('MMM D'),
      long: d.format('ddd, MMM D'),
      sub: `${opponent ? `vs ${opponent} · ` : ''}Game ${(item.index ?? 0) + 1}`,
      opponent,
      isAverage: false,
    };
  }, [league, isLiveDate]);
  const gamesShown = gameItems.map((g) => ({ ...g, label: gameLabel(g) }));

  // Data: one request per location per date, shared by every subject in that location
  const [dataByKey, setDataByKey] = useState({});
  const [pending, setPending] = useState([]);
  const requested = useRef(new Set());
  useEffect(() => {
    const needed = [];
    subjects.forEach(({ subject }) => {
      gameItems.forEach((g) => g.dates.forEach((d) => {
        const key = locationDayKey(subject.vertical, subject.location.id, d.date);
        if (!requested.current.has(key) && !needed.some((n) => n.key === key)) {
          needed.push({ key, vertical: subject.vertical, location: subject.location, date: d.date });
        }
      }));
    });
    needed.forEach((n) => {
      requested.current.add(n.key);
      setPending((p) => [...p, n.key]);
      fetchLocationDay(n.vertical, n.location, authCtx, n.date)
        .then((raw) => setDataByKey((prev) => ({ ...prev, [n.key]: raw })))
        .catch((error) => {
          if (error?.status === 401) {
            authCtx.logout();
            navigate('/auth');
            return;
          }
          console.error(`Analytics: could not load ${n.key}`, error);
          // Allow another try next time it's picked
          requested.current.delete(n.key);
          setDataByKey((prev) => ({ ...prev, [n.key]: { failed: true } }));
        })
        .finally(() => setPending((p) => p.filter((k) => k !== n.key)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, gameItems]);
  const loading = pending.length > 0;

  // Minute series per subject per date, computed once per response
  const seriesCache = useRef(new Map());
  const seriesFor = useCallback((subject, date) => {
    const dayKey = locationDayKey(subject.vertical, subject.location.id, date);
    const raw = dataByKey[dayKey];
    if (!raw || raw.failed) return null;
    const cacheKey = `${subject.key}|${dayKey}`;
    const hit = seriesCache.current.get(cacheKey);
    if (hit && hit.raw === raw) return hit.series;
    const series = subjectSeries(subject, raw, date);
    seriesCache.current.set(cacheKey, { raw, series });
    return series;
  }, [dataByKey]);
  const isLoaded = (subject, date) => !!dataByKey[locationDayKey(subject.vertical, subject.location.id, date)];

  // Each subject: a summary per game (or series average) and across all of them
  const rows = useMemo(() => subjects.map(({ subject, color }) => {
    const perGame = gamesShown.map((g) => {
      const loaded = g.dates.every((d) => isLoaded(subject, d.date));
      const summary = combineDays(g.dates.map((d) => summarizeDay(seriesFor(subject, d.date), d, timeRange, d.date)));
      return { game: g, loaded, summary };
    });
    const allDates = Array.from(new Map(gamesShown.flatMap((g) => g.dates).map((d) => [d.date, d])).values());
    const days = allDates.map((d) => summarizeDay(seriesFor(subject, d.date), d, timeRange, d.date)).filter((d) => d && d.hasData);
    const overall = combineDays(days);
    return { subject, color, perGame, days, overall, loaded: perGame.every((p) => p.loaded) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [picked, gameItems, dataByKey, timeRange, seriesFor]);

  const hasSelection = subjects.length > 0 && gameItems.length > 0;
  const anyData = rows.some((r) => r.overall);
  const noData = rows.filter((r) => r.loaded && !r.overall);

  // Headline numbers
  const kpi = useMemo(() => {
    let longest = null;
    rows.forEach((r) => r.perGame.forEach((pg) => {
      const p = pg.summary?.queue?.peak;
      if (p && (!longest || p.value > longest.value)) longest = { ...p, subject: r.subject, game: pg.game };
    }));
    const byAvg = rows.filter((r) => r.overall?.queue).sort((a, b) => b.overall.queue.avg - a.overall.queue.avg);
    const byEntries = rows.filter((r) => r.overall?.entries).sort((a, b) => b.overall.entries.total - a.overall.entries.total);
    const byTx = rows.filter((r) => r.overall?.transaction).sort((a, b) => b.overall.transaction.avg - a.overall.transaction.avg);
    const byOcc = rows.filter((r) => r.overall?.occupancy).sort((a, b) => b.overall.occupancy.peak.value - a.overall.occupancy.peak.value);
    return { longest, byAvg, byEntries, byTx, byOcc };
  }, [rows]);

  const insights = useMemo(() => buildAnalyticsInsights(rows.filter((r) => r.overall), gamesShown), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Table
  const statOptions = TABLE_STATS.filter((s) => rows.some((r) => r.subject.metrics.includes(s.metric)));
  const [statKey, setStatKey] = useState('avgQueue');
  const stat = statOptions.find((s) => s.key === statKey) || statOptions[0];
  const tableRows = useMemo(() => {
    if (!stat) return [];
    return rows
      .map((r) => {
        // "Average" means per game, averaged: for peaks that's the average of each game's peak
        let value = r.overall ? stat.get(r.overall) : null;
        if (stat.at) {
          const peaks = r.days.map((d) => stat.get(d)).filter((v) => typeof v === 'number');
          value = peaks.length ? peaks.reduce((a, b) => a + b, 0) / peaks.length : null;
        }
        return { ...r, measured: r.subject.metrics.includes(stat.metric), value };
      })
      .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
  }, [rows, stat]);
  const tableMax = stat ? Math.max(1, ...tableRows.flatMap((r) => r.perGame.map((pg) => (pg.summary ? stat.get(pg.summary) || 0 : 0)))) : 1;
  const showGameColumns = !averageGames;
  const showAverageColumn = averageGames || gameItems.length > 1;

  // Chart
  const metricOptions = METRIC_ORDER
    .filter((m) => subjects.some(({ subject }) => subject.metrics.includes(m)))
    .map((m) => ({ value: m, label: METRICS[m].label }));
  const [metric, setMetric] = useState('queue');
  const chartMetric = metricOptions.some((o) => o.value === metric) ? metric : metricOptions[0]?.value;
  const [countMode, setCountMode] = useState('total');
  const [align, setAlign] = useState('start');
  const [resolution, setResolution] = useState(15);
  // One chart, a small chart per game, or a small chart per location
  const [splitBy, setSplitBy] = useState('none');
  const isFlow = chartMetric === 'entries' || chartMetric === 'exits';
  const runningTotal = isFlow && countMode === 'total';
  const alignLocked = averageGames || gameItems.some((g) => g.dates.length > 1);
  const chartAlign = alignLocked ? 'start' : align;

  const chartLines = useMemo(() => (chartMetric ? buildChartLines({
    subjects, games: gamesShown, metric: chartMetric, seriesFor, window: timeRange, align: chartAlign, resolution, runningTotal, averageGames,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }) : []), [picked, gameItems, chartMetric, seriesFor, timeRange, chartAlign, resolution, runningTotal, averageGames]);

  const every = resolution === 60 ? 'hour' : resolution === 1 ? 'minute' : `${resolution} min`;
  const avgLabel = resolution === 60 ? 'hourly averages' : resolution === 1 ? 'minute by minute' : `${resolution}-minute averages`;
  const chartMeta = {
    queue: { yTitle: 'Queue (min)', subtitle: `Queue at each location, ${avgLabel}. Stands, gates and restrooms are averaged across their sections, gates or restrooms.`, formatValue: (v) => `${v} min` },
    entries: runningTotal
      ? { yTitle: 'Entries so far', subtitle: 'Running total of entries at each location, from the start of the time window.', formatValue: (v) => `${fmt(v)} entries` }
      : { yTitle: `Entries per ${every}`, subtitle: `Entries at each location, per ${every}.`, formatValue: (v) => `${fmt(v)} entries` },
    exits: runningTotal
      ? { yTitle: 'Exits so far', subtitle: 'Running total of exits at each location, from the start of the time window.', formatValue: (v) => `${fmt(v)} exits` }
      : { yTitle: `Exits per ${every}`, subtitle: `Exits at each location, per ${every}.`, formatValue: (v) => `${fmt(v)} exits` },
    occupancy: { yTitle: 'People in store', subtitle: `People in each store, ${avgLabel}.`, formatValue: (v) => `${fmt(v)} people` },
    count: { yTitle: 'People count', subtitle: `People counted at each stand, ${avgLabel}.`, formatValue: (v) => `${fmt(v)} people` },
    transaction: { yTitle: 'Transaction time', subtitle: `Time per transaction, ${avgLabel}.`, formatValue: (v) => formatSeconds(v), formatTick: (v) => formatSeconds(v) },
  }[chartMetric] || {};
  const chartSubjects = subjects
    .filter(({ subject }) => subject.metrics.includes(chartMetric))
    .map(({ subject, color }) => ({ key: subject.key, name: subject.name, color }));
  const hiddenFromChart = subjects.length - chartSubjects.length;

  const isSessionValid = authCtx.loggedIn && moment.unix(authCtx.tokenExpirationTime) > moment();
  if (!isSessionValid) {
    return (
      <div className="p-4">
        <Link to="/auth">Not Authorized. Please Log-In with given credentials or contact support.</Link>
      </div>
    );
  }
  if (scheduleLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="large" />
      </div>
    );
  }

  const cellFor = (row, pg) => {
    if (!row.measured) return <span className="gx-ov-none">Not measured</span>;
    if (!pg.loaded) return <span className="cx-skel cx-skel-line an-skel" />;
    const v = pg.summary ? stat.get(pg.summary) : null;
    if (v == null) return <span className="gx-ov-none">—</span>;
    const at = stat.at ? stat.at(pg.summary) : null;
    return (
      <>
        <strong style={stat.queueColored ? { color: queueColor(v) } : undefined}>{stat.format(v)}</strong>
        {stat.bar && (
          <span className="gx-ov-bar">
            <i style={{ width: `${(v / tableMax) * 100}%`, background: row.color }} />
          </span>
        )}
        {at && at.minute != null && (
          <small>{pg.game.dates.length > 1 && at.date ? `${moment(at.date).format('MMM D')}, ` : ''}{minuteToClock(at.minute)}</small>
        )}
      </>
    );
  };

  return (
    <div className="cx-page an-page">
      {loading && view === 'compare' && (
        <div style={{ position: 'fixed', top: 0, left: 75, right: 0, zIndex: 9999 }}>
          <LinearProgress
            sx={{ backgroundColor: 'rgba(2, 166, 147, 0.25)', height: 4, '& .MuiLinearProgress-bar': { backgroundColor: 'rgb(2, 166, 147)' } }}
          />
        </div>
      )}

      <div className="sticky-top cx-topbar">
        <div className="cx-header">
          <div className="cx-header-identity">
            <span className="an-header-icon"><Icon name="pie-chart-fill" /></span>
            <div className="cx-header-divider" />
            <div style={{ minWidth: 0 }}>
              <div className="cx-eyebrow">{view === 'alerts' ? 'Concessions' : 'Compare locations'}</div>
              <h1 className="cx-header-title">Analytics</h1>
            </div>
          </div>
          <div className="cx-header-actions">
            <div className="an-header-note">
              {view === 'alerts'
                ? 'How often queues reached the alert threshold, and how fast they recovered.'
                : 'Compare any retail stores, concessions, gates and restrooms across the same games.'}
            </div>
            {hasQueueStands && (
              <div className="cx-segmented" role="tablist">
                <button type="button" role="tab" aria-selected={view === 'compare'} className={`cx-segmented-btn ${view === 'compare' ? 'active' : ''}`} onClick={() => setView('compare')}>
                  <Icon name="bar-chart-line" />
                  Compare
                </button>
                <button type="button" role="tab" aria-selected={view === 'alerts'} className={`cx-segmented-btn ${view === 'alerts' ? 'active' : ''}`} onClick={() => setView('alerts')}>
                  <Icon name="bell" />
                  Queue alerts
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cx-content rx-ov an-content">
        {view === 'alerts' ? (
          <QueueAlertsView authCtx={authCtx} games={games} league={league} season={currentSeason} todayKey={todayKey} />
        ) : (
        <>
        {/* Compare bar: locations, games and the time window */}
        <div className="mb-4 cx-ov-bar-wrap">
          <Panel className="cx-ov-bar an-bar">
            <div className="an-bar-row">
              <div className="cx-ov-bar-label">
                Locations
                <span className="rx-ov-count">{picked.length} of {MAX_LOCATIONS}</span>
              </div>
              <div className="cx-ov-chips">
                {subjects.map(({ subject, color }) => (
                  <span key={subject.key} className="cx-ov-chip cx-fade-in an-chip">
                    <span className="cx-ov-swatch" style={{ background: color }} />
                    <SubjectIcon subject={subject} size={20} />
                    <span className="cx-ov-chip-text rx-ov-chip-text">
                      <span>{subject.shortName || subject.name}</span>
                      {subject.parentName && <span className="rx-ov-chip-window">{subject.parentName}</span>}
                    </span>
                    <button type="button" className="cx-ov-chip-remove" aria-label={`Remove ${subject.name}`} onClick={() => toggleSubject(subject)}>
                      <Icon name="x" />
                    </button>
                  </span>
                ))}
                <div className="cx-ov-add" ref={locRef}>
                  <button
                    type="button"
                    className="cx-ov-add-btn"
                    onClick={() => setLocPickerOpen((o) => !o)}
                    disabled={picked.length >= MAX_LOCATIONS && !locPickerOpen}
                    aria-expanded={locPickerOpen}
                  >
                    <Icon name="plus-lg" />
                    {picked.length >= MAX_LOCATIONS ? `Max ${MAX_LOCATIONS} locations` : picked.length ? 'Add location' : 'Add locations to compare'}
                  </button>
                  {locPickerOpen && (
                    <LocationPicker
                      catalog={catalog}
                      selectedKeys={picked.map((p) => p.key)}
                      colorOf={colorOf}
                      onToggle={toggleSubject}
                      full={picked.length >= MAX_LOCATIONS}
                      onClose={() => setLocPickerOpen(false)}
                    />
                  )}
                </div>
                {picked.length > 0 && (
                  <button type="button" className="cx-ov-clear" onClick={() => setPicked([])}>Clear</button>
                )}
              </div>
            </div>

            <div className="an-bar-row">
              <div className="cx-ov-bar-label">
                Games
                <span className="rx-ov-count">{gameItems.length} of {MAX_GAMES}</span>
              </div>
              <div className="cx-ov-chips">
                {gamesShown.map((g) => (
                  <span key={g.key} className="cx-ov-chip cx-fade-in an-chip">
                    <DashSample dash={g.dash} color={g.color} />
                    <span className="cx-ov-chip-text rx-ov-chip-text">
                      <span>
                        {g.label.isAverage && <span className="an-avg-badge">AVG</span>}
                        {g.label.short}
                        {g.label.opponent && <span className="cx-ov-chip-sub"> · {g.label.opponent}</span>}
                      </span>
                      {g.label.live && <span className="rx-ov-chip-window">In progress: so far</span>}
                    </span>
                    <button type="button" className="cx-ov-chip-remove" aria-label={`Remove ${g.label.short}`} onClick={() => setGameItems((prev) => prev.filter((x) => x.key !== g.key))}>
                      <Icon name="x" />
                    </button>
                  </span>
                ))}
                <div className="cx-ov-add" ref={gameRef}>
                  <button
                    type="button"
                    className="cx-ov-add-btn"
                    onClick={() => setGamePickerOpen((o) => !o)}
                    disabled={gameItems.length >= MAX_GAMES && !gamePickerOpen}
                    aria-expanded={gamePickerOpen}
                  >
                    <Icon name="plus-lg" />
                    {gameItems.length >= MAX_GAMES ? `Max ${MAX_GAMES} games` : gameItems.length ? 'Add game' : 'Add games to compare'}
                  </button>
                  {gamePickerOpen && (
                    <GamePicker
                      games={games}
                      league={league}
                      isLiveDate={isLiveDate}
                      currentSeason={currentSeason}
                      setCurrentSeason={setCurrentSeason}
                      gameItems={gameItems}
                      onToggleGame={toggleGame}
                      onToggleSeries={toggleSeries}
                      full={gameItems.length >= MAX_GAMES}
                      onClose={() => setGamePickerOpen(false)}
                    />
                  )}
                </div>
                {gameItems.length > 0 && (
                  <button type="button" className="cx-ov-clear" onClick={() => setGameItems([])}>Clear</button>
                )}
                {gameItems.length > 1 && (
                  <div className="an-avg-switch">
                    <ChartSegmented
                      options={[{ value: false, label: 'Each game' }, { value: true, label: 'Average games' }]}
                      value={averageGames}
                      onChange={setAverageGames}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="cx-ov-bar-window an-bar-window">
              <TimeWindow
                compact
                startOffset={timeRange.startOffset}
                endOffset={timeRange.endOffset}
                onStartChange={(v) => setTimeRange((t) => ({ ...t, startOffset: v }))}
                onEndChange={(v) => setTimeRange((t) => ({ ...t, endOffset: v }))}
                onPreset={(start, end) => setTimeRange({ startOffset: start, endOffset: end })}
                info={<InfoTip title="Time window" text="Every number, finding and chart below uses this window, measured from each game's start." />}
              />
            </div>
          </Panel>
        </div>

        <Presence
          show={hasSelection}
          className="cx-ov-presence"
          fallback={(
            <div className="cx-empty-state cx-ov-empty-main">
              <Icon name="pie-chart" />
              <div className="cx-empty-title">
                {!subjects.length && !gameItems.length ? 'Nothing to compare yet' : !subjects.length ? 'Add a location' : 'Add a game'}
              </div>
              <div>
                {!subjects.length
                  ? <>Use <strong>Add locations to compare</strong> to pick up to {MAX_LOCATIONS} retail stores, concession stands or sections, gates and restrooms</>
                  : 'Pick at least one location'}
                {!gameItems.length
                  ? <>, then <strong>Add games to compare</strong> to pick up to {MAX_GAMES} games or series averages.</>
                  : '.'}
              </div>
            </div>
          )}
        >
          <div className="rx-ov-main">
            {noData.length > 0 && (
              <div className="cx-notice">
                <Icon name="info-circle" />
                No data for {noData.map((r) => r.subject.name).join(', ')} in the selected games and time window.
              </div>
            )}

            {/* Headline numbers */}
            {anyData && (
              <div className="cx-kpi-grid cx-kpi-grid-auto">
                {kpi.longest && (
                  <KpiCard
                    label="Longest queue"
                    info="The longest queue reading at any selected location, in any selected game, during the time window."
                    value={<>{kpi.longest.value}<span className="cx-unit">min</span></>}
                    accent={queueColor(kpi.longest.value)}
                  >
                    <div className="cx-kpi-sub">{kpi.longest.subject.name}</div>
                    <div className="cx-kpi-foot">
                      {kpi.longest.date ? moment(kpi.longest.date).format('ddd, MMM D') : kpi.longest.game.label.long}, {minuteToClock(kpi.longest.minute)}
                    </div>
                  </KpiCard>
                )}
                {kpi.byAvg.length > 0 && (
                  <KpiCard
                    label={kpi.byAvg.length > 1 ? 'Longest average queue' : 'Average queue'}
                    info="Average queue during the time window, per game, averaged across the selected games."
                    value={<>{kpi.byAvg[0].overall.queue.avg.toFixed(1)}<span className="cx-unit">min</span></>}
                    accent={queueColor(kpi.byAvg[0].overall.queue.avg)}
                  >
                    <div className="cx-kpi-sub">{kpi.byAvg[0].subject.name}</div>
                    <div className="cx-kpi-foot">
                      {kpi.byAvg.length > 1
                        ? `Shortest: ${kpi.byAvg[kpi.byAvg.length - 1].subject.name} (${kpi.byAvg[kpi.byAvg.length - 1].overall.queue.avg.toFixed(1)} min)`
                        : 'Per game, averaged'}
                    </div>
                  </KpiCard>
                )}
                {kpi.byEntries.length > 0 && (
                  <KpiCard
                    label={kpi.byEntries.length > 1 ? 'Most entries' : 'Entries'}
                    info="Entries during the time window, per game, averaged across the selected games."
                    value={fmt(kpi.byEntries[0].overall.entries.total)}
                  >
                    <div className="cx-kpi-sub">{kpi.byEntries[0].subject.name}</div>
                    <div className="cx-kpi-foot">
                      {kpi.byEntries[0].overall.entries.rush?.offset != null
                        ? `Busiest ${relativeToStart(kpi.byEntries[0].overall.entries.rush.offset)}`
                        : 'Per game, averaged'}
                    </div>
                  </KpiCard>
                )}
                {kpi.byTx.length > 0 ? (
                  <KpiCard
                    label={kpi.byTx.length > 1 ? 'Slowest transactions' : 'Transaction time'}
                    info="Average time per transaction during the time window, per game, averaged across the selected games."
                    value={formatSeconds(kpi.byTx[0].overall.transaction.avg)}
                  >
                    <div className="cx-kpi-sub">{kpi.byTx[0].subject.name}</div>
                    <div className="cx-kpi-foot">
                      {kpi.byTx.length > 1 ? `Quickest: ${kpi.byTx[kpi.byTx.length - 1].subject.name} (${formatSeconds(kpi.byTx[kpi.byTx.length - 1].overall.transaction.avg)})` : 'Per game, averaged'}
                    </div>
                  </KpiCard>
                ) : kpi.byOcc.length > 0 && (
                  <KpiCard
                    label="Peak occupancy"
                    info="The most people in a selected store at once, during the time window."
                    value={fmt(kpi.byOcc[0].overall.occupancy.peak.value)}
                  >
                    <div className="cx-kpi-sub">{kpi.byOcc[0].subject.name}</div>
                    <div className="cx-kpi-foot">
                      {kpi.byOcc[0].overall.occupancy.peak.date ? `${moment(kpi.byOcc[0].overall.occupancy.peak.date).format('ddd, MMM D')}, ` : ''}
                      {minuteToClock(kpi.byOcc[0].overall.occupancy.peak.minute)}
                    </div>
                  </KpiCard>
                )}
              </div>
            )}

            {insights.length > 0 && (
              <div className="mb-4">
                <Panel>
                  <PanelHeader title="What stands out" subtitle="Key findings across the selected locations and games, most important first." />
                  <OverviewInsights insights={insights} />
                </Panel>
              </div>
            )}

            {/* Locations across the games */}
            {stat && (
              <Panel className="rx-ov-panel">
                <PanelHeader
                  title="Locations across the games"
                  subtitle={`${stat.label} at each location${showGameColumns && gameItems.length > 1 ? ' in each game' : ''}, during the time window. Highest first.`}
                  actions={statOptions.length > 1 ? (
                    <ChartSegmented options={statOptions.map((s) => ({ value: s.key, label: s.label }))} value={stat.key} onChange={setStatKey} />
                  ) : null}
                />
                <div className="gx-ov-table-wrap">
                  <table className="gx-ov-table an-table">
                    <thead>
                      <tr>
                        <th className="gx-ov-gate-col">Location</th>
                        {showGameColumns && gamesShown.map((g) => (
                          <th key={g.key}>
                            <span className="an-th-game">
                              {gameItems.length > 1 && <DashSample dash={g.dash} color={g.color} />}
                              {g.label.isAverage && <span className="an-avg-badge">AVG</span>}
                              {g.label.short}
                              {g.label.live && <span className="an-live-tag">Live</span>}
                            </span>
                          </th>
                        ))}
                        {showAverageColumn && <th>{averageGames ? 'Average of the games' : 'Average'}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((r) => (
                        <tr key={r.subject.key}>
                          <td className="gx-ov-gate-col">
                            <span className="gx-ov-gate an-row-name">
                              <span className="cx-ov-swatch" style={{ background: r.color }} />
                              <SubjectIcon subject={r.subject} size={24} />
                              <span>
                                {r.subject.shortName || r.subject.name}
                                {r.subject.parentName && <small className="an-row-parent">{r.subject.parentName}</small>}
                              </span>
                            </span>
                          </td>
                          {showGameColumns && r.perGame.map((pg) => <td key={pg.game.key}>{cellFor(r, pg)}</td>)}
                          {showAverageColumn && (
                            <td className="gx-ov-avg">
                              {!r.measured ? <span className="gx-ov-none">Not measured</span>
                                : !r.loaded ? <span className="cx-skel cx-skel-line an-skel" />
                                  : r.value == null ? '—'
                                    : <span style={stat.queueColored ? { color: queueColor(r.value) } : undefined}>{stat.format(r.value)}</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            {/* Through the day */}
            {chartMetric && (
              <AnalyticsCompareChart
                title={`${METRICS[chartMetric].label} through the day`}
                subtitle={`${chartMeta.subtitle}${hiddenFromChart > 0 ? ` ${hiddenFromChart} selected location${hiddenFromChart === 1 ? " doesn't" : "s don't"} measure this.` : ''}`}
                metricOptions={metricOptions}
                metric={chartMetric}
                onMetric={setMetric}
                align={chartAlign}
                onAlign={setAlign}
                alignLocked={alignLocked}
                resolution={resolution}
                onResolution={!runningTotal ? setResolution : undefined}
                extraControls={isFlow ? (
                  <ChartSegmented
                    options={[{ value: 'total', label: 'Running total' }, { value: 'interval', label: 'Per interval' }]}
                    value={countMode}
                    onChange={setCountMode}
                  />
                ) : null}
                splitBy={splitBy}
                onSplitBy={setSplitBy}
                lines={chartLines}
                subjects={chartSubjects}
                games={averageGames ? [] : gamesShown}
                unit={METRICS[chartMetric].unit}
                yTitle={chartMeta.yTitle}
                formatValue={chartMeta.formatValue}
                formatTick={chartMeta.formatTick}
              />
            )}
          </div>
        </Presence>
        </>
        )}
      </div>
    </div>
  );
}
